/**
 * Catálogo de permissões do Portal do Gerador — GR-637.
 *
 * Cada entrada é uma client role do `portal-gerador` no realm `generator`. O
 * usuário ganha essas roles pela membership nos grupos do catálogo (ver
 * `permissionProfiles.ts`), e o Keycloak as publica no token em
 * `resource_access["portal-gerador"].roles`.
 *
 * A fonte de verdade é `management/src/generator.tf` no repositório keycloak.
 * Este arquivo é o espelho tipado, e os dois precisam andar juntos: role que
 * existe lá e não existe aqui simplesmente não é reconhecida pelo Portal.
 *
 * NADA aqui bloqueia tela ainda. A Onda 1 é que liga o bloqueio, e só depois
 * da conferência da migração dos usuários existentes (GR-561, regra 16).
 */

/**
 * Permissões já suportadas por uma tela ou ação que existe hoje no Portal.
 */
export const PERMISSIONS = {
  // --- Painel e indicadores ---
  VIEW_DASHBOARD: "view-dashboard",
  VIEW_ENVIRONMENTAL_INDICATORS: "view-environmental-indicators",
  VIEW_ENVIRONMENTAL_CERTIFICATES: "view-environmental-certificates",

  // --- Agenda de coletas ---
  VIEW_UPCOMING_COLLECTS: "view-upcoming-collects",
  DOWNLOAD_UPCOMING_COLLECTS_CSV: "download-upcoming-collects-csv",
  DOWNLOAD_UPCOMING_COLLECTS_MTR_BULK: "download-upcoming-collects-mtr-bulk",

  // --- Histórico de coletas ---
  VIEW_COLLECTS_HISTORY: "view-collects-history",
  DOWNLOAD_COLLECTS_HISTORY_CSV: "download-collects-history-csv",
  DOWNLOAD_COLLECTS_HISTORY_MTR_BULK: "download-collects-history-mtr-bulk",

  /** Evidências nas duas telas de coleta — substitui a legada `view-collect-evidence`. */
  VIEW_COLLECTS_EVIDENCE: "view-collects-evidence",

  // --- Coleta sob demanda ---
  VIEW_ON_DEMAND_COLLECTS: "view-on-demand-collects",
  REQUEST_ON_DEMAND_COLLECT_SCHEDULED: "request-on-demand-collect-scheduled",
  REQUEST_ON_DEMAND_COLLECT_EXTRA: "request-on-demand-collect-extra",
  CANCEL_ON_DEMAND_COLLECT: "cancel-on-demand-collect",
  APPROVE_ON_DEMAND_COLLECT_PRICING: "approve-on-demand-collect-pricing",
  REQUEST_CARBON_CERTIFICATE: "request-carbon-certificate",

  // --- Financeiro ---
  VIEW_INVOICES: "view-invoices",
  EDIT_INVOICE_DUE_DATE: "edit-invoice-due-date",
  DISPUTE_MEASUREMENT: "dispute-measurement",

  // --- Análise e contrato ---
  VIEW_CONTRACT_PDF: "view-contract-pdf",
  VIEW_PERFORMANCE_DASHBOARD: "view-performance-dashboard",
  VIEW_COMPLIANCE_DASHBOARD: "view-compliance-dashboard",

  // --- Gestão da empresa ---
  MANAGE_USERS: "manage-users",
  EDIT_GENERATOR_LOGO: "edit-generator-logo",
  MANAGE_ESTABLISHMENT_CATEGORIES: "manage-establishment-categories",

  // --- Acesso base (todo mundo do grupo `default`) ---
  VIEW_SUPPORT: "view-support",
  EDIT_OWN_PROFILE: "edit-own-profile",
  VIEW_HELP_CENTER: "view-help-center",
  ANSWER_CSAT: "answer-csat",
  VIEW_TERMS: "view-terms",

  // --- Legadas ---
  // Anteriores ao catálogo, preservadas intactas no Terraform porque já estão
  // atribuídas a usuário real (ex.: Daki, GR-563). Não são concedidas por
  // nenhum grupo: quem tem, tem por grant direto na conta.
  LEGACY_VIEW_COLLECT_EVIDENCE: "view-collect-evidence",
  LEGACY_VIEW_ENVIRONMENTAL_DATA_JADLOG: "view-environmental-data-jadlog",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/** Todas as permissões conhecidas pelo Portal. */
export const ALL_PERMISSIONS: readonly Permission[] = Object.freeze(
  Object.values(PERMISSIONS),
);

/**
 * Permissões marcadas `[Planejado]` no Terraform: existem no Keycloak, mas a
 * funcionalidade ainda não foi construída. Bloquear por elas na Onda 1 não faz
 * sentido — não há tela para liberar nem para negar.
 */
export const PLANNED_PERMISSIONS: ReadonlySet<Permission> = new Set<Permission>([
  PERMISSIONS.REQUEST_CARBON_CERTIFICATE,
  PERMISSIONS.EDIT_INVOICE_DUE_DATE,
  // `DISPUTE_MEASUREMENT` saiu daqui: a contestação (GR-608) existe e é travada
  // em `InvoiceDetailContent`.
  PERMISSIONS.VIEW_CONTRACT_PDF,
  PERMISSIONS.VIEW_PERFORMANCE_DASHBOARD,
  PERMISSIONS.VIEW_COMPLIANCE_DASHBOARD,
  PERMISSIONS.EDIT_GENERATOR_LOGO,
  PERMISSIONS.MANAGE_ESTABLISHMENT_CATEGORIES,
]);

const KNOWN = new Set<string>(ALL_PERMISSIONS);

/**
 * `true` quando a string é uma permissão do catálogo.
 *
 * Role desconhecida vinda do token não é erro: pode ser uma role criada no
 * Keycloak antes deste arquivo acompanhar. Ela é preservada no conjunto
 * efetivo do usuário, só não ganha tipo.
 */
export function isKnownPermission(value: string): value is Permission {
  return KNOWN.has(value);
}
